import Component from '@glimmer/component';
import IconBadge, { type IconBadgeColor, type IconBadgeSize } from 'tio-ui/components/icon-badge';
import type { ComponentLike } from '@glint/template';
import {
  PhGraduationCap,
  PhBank,
  PhPiggyBank,
  PhScroll,
  PhDatabase,
  PhCompass,
} from 'ember-phosphor-icons';
import { productForReport, type FilterableReport } from '../../utils/report-filtering.ts';
import type { ProductCode } from '../../utils/dataset-utils.ts';
import { configFor, type ReportKind } from './kind-config.ts';

export type ProductIcon = ComponentLike<{ Args: { size: number }; Element: SVGElement }>;

/** Single source for product icon + badge color (rail items, cards, rows). */
export const PRODUCT_BADGES: Record<ProductCode, { icon: ProductIcon; color: IconBadgeColor }> = {
  TAS: { icon: PhGraduationCap, color: 'purple' },
  SLR: { icon: PhBank, color: 'green' },
  SYF: { icon: PhPiggyBank, color: 'teal' },
  PSLF: { icon: PhScroll, color: 'amber' },
  STRATEGYFINDER: { icon: PhCompass, color: 'blue' },
  Core: { icon: PhDatabase, color: 'gray' },
};

interface ProductIconBadgeSignature {
  Args: {
    report: FilterableReport;
    size?: IconBadgeSize;
    /** Which surface owns this badge. Reports group by product; dashboards use a fallback icon. Defaults to 'report'. */
    kind?: ReportKind;
  };
  Element: HTMLDivElement;
}

/** Product-colored icon badge (design: soft color pair per product). */
export default class ProductIconBadge extends Component<ProductIconBadgeSignature> {
  get kind() {
    return this.args.kind ?? 'report';
  }

  get config() {
    return configFor(this.kind);
  }

  get badge() {
    return PRODUCT_BADGES[productForReport(this.args.report)];
  }

  <template>
    {{#if this.config.productGrouping}}
      <IconBadge
        @icon={{this.badge.icon}}
        @color={{this.badge.color}}
        @size={{if @size @size "sm"}}
        ...attributes
      />
    {{else}}
      <IconBadge @icon={{this.config.fallbackIcon}} @size={{if @size @size "sm"}} ...attributes />
    {{/if}}
  </template>
}
